import { OptionallyVisible } from 'components';
import { FormikErrors } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/buttons';
import { Panel } from 'components/containers';
import { ErrorMessage } from 'components/form/formField/styles';
import { FormLayout } from 'components/form/formLayout/formLayout';
import { FormFieldDescription } from 'components/form/formLayout/types';
import { IconAdd } from 'components/icons/add';
import IconRemove from 'components/icons/dash';
import { Input, InputType } from 'components/inputs';

import { OrderItemType } from 'types/pwcOrder';

import {
  FORM_FIELDS,
  FormFieldType,
  INITIAL_NAMES_FOR_DISCOUNT_ITEMS,
  INITIAL_NAMES_FOR_TAX_ITEMS,
  ITEM_FORM_FIELDS,
  ITEM_FORM_INITIAL_VALUES,
  LABELS,
} from '../keys';
import { ImageUploadOutcome, Item } from '../types';
import OrderSummaryItem from './item';
import { FieldsWrapper, FormWrapper, LightText, PaddingWrapper, RemoveButton, Separator } from './styles';

interface OrderSummaryProps {
  readOnlyMode: boolean;
  errors?: FormikErrors<Record<string, string>>;
  uploadErrors: ImageUploadOutcome[];
  setFieldValue: (field: FormFieldType, value: Item[]) => void;
}

type IdGen = (type: OrderItemType) => string;

const OrderSummary = ({ readOnlyMode, errors, setFieldValue, uploadErrors }: OrderSummaryProps) => {
  const { t } = useTranslation();

  function makeInitialItems(
    types: OrderItemType[],
    nameMap: Partial<Record<OrderItemType, string>>,
    fallbackName: string,
    idGen: IdGen = (type) => type,
    isRemovable: boolean = false,
  ): Record<string, Item> {
    return types.reduce(
      (acc, type) => {
        const id = idGen(type);
        acc[id] = {
          id,
          name: nameMap[type] ?? fallbackName,
          fiatAmount: ITEM_FORM_INITIAL_VALUES[ITEM_FORM_FIELDS.FIAT_AMOUNT],
          imageUrl: ITEM_FORM_INITIAL_VALUES[ITEM_FORM_FIELDS.IMAGE_URL],
          type,
          isRemovable,
        };
        return acc;
      },
      {} as Record<string, Item>,
    );
  }

  const [items, setItems] = useState<{ [id: string]: Item }>(() =>
    makeInitialItems([OrderItemType.Product], {}, ITEM_FORM_INITIAL_VALUES[ITEM_FORM_FIELDS.NAME], () => Date.now().toString()),
  );

  const [taxItems, setTaxItems] = useState<{ [id: string]: Item }>(() =>
    makeInitialItems(
      [OrderItemType.Tax, OrderItemType.Delivery, OrderItemType.Tip, OrderItemType.Gratuity],
      INITIAL_NAMES_FOR_TAX_ITEMS,
      ITEM_FORM_INITIAL_VALUES[ITEM_FORM_FIELDS.NAME],
    ),
  );

  const [discountItems, setDiscountItems] = useState<{ [id: string]: Item }>(() =>
    makeInitialItems(
      [OrderItemType.Discount],
      INITIAL_NAMES_FOR_DISCOUNT_ITEMS,
      ITEM_FORM_INITIAL_VALUES[ITEM_FORM_FIELDS.NAME],
      () => Date.now().toString(),
      false,
    ),
  );

  const onAddItem = () => {
    const newProduct = makeInitialItems([OrderItemType.Product], {}, ITEM_FORM_INITIAL_VALUES[ITEM_FORM_FIELDS.NAME], () =>
      Date.now().toString(),
    );

    setItems((prev) => ({
      ...prev,
      ...newProduct,
    }));
  };

  const onRemoveItem = (id: string) => {
    const updatedItems = Object.fromEntries(Object.entries(items).filter(([key]) => key !== id));

    setItems(updatedItems);
  };

  const onAddTaxItem = () => {
    const newItem = makeInitialItems([OrderItemType.Custom], {}, ITEM_FORM_INITIAL_VALUES[ITEM_FORM_FIELDS.NAME], () =>
      Date.now().toString(),
    );
    setTaxItems((prev) => ({ ...prev, ...newItem }));
  };

  const onRemoveTaxItem = (id: string) => {
    const updatedItems = Object.fromEntries(Object.entries(taxItems).filter(([key]) => key !== id));

    setTaxItems(updatedItems);
  };

  const onAddDiscountItem = () => {
    const newItem = makeInitialItems(
      [OrderItemType.Discount],
      {},
      ITEM_FORM_INITIAL_VALUES[ITEM_FORM_FIELDS.NAME],
      () => Date.now().toString(),
      true,
    );
    setDiscountItems((prev) => ({ ...prev, ...newItem }));
  };

  const onRemoveDiscountItem = (id: string) => {
    const updatedItems = Object.fromEntries(Object.entries(discountItems).filter(([key]) => key !== id));
    setDiscountItems(updatedItems);
  };

  const onItemChange = (id: string, type: OrderItemType) => (item: Omit<Item, 'type'>) => {
    const updatedItems = { ...items };
    updatedItems[id] = {
      ...updatedItems[id],
      ...item,
      type,
    };

    setItems(updatedItems);
  };

  const onTaxItemChange = (id: string, field: string) => (value: string) => {
    const updatedItems = { ...taxItems };
    updatedItems[id] = {
      ...updatedItems[id],
      [field]: value,
    };

    setTaxItems(updatedItems);
  };

  const onDiscountItemChange = (id: string, field: string) => (value: string) => {
    const updatedItems = { ...discountItems };
    updatedItems[id] = {
      ...updatedItems[id],
      [field]: value,
    };

    setDiscountItems(updatedItems);
  };

  useEffect(() => {
    const removeFlag = ({ isRemovable, ...rest }: Item) => rest;

    const clearedItems = [
      ...Object.values(items).map(removeFlag),
      ...Object.values(taxItems).map(removeFlag),
      ...Object.values(discountItems).map(removeFlag),
    ];
    setFieldValue(FORM_FIELDS.ITEMS, clearedItems);
  }, [items, taxItems, discountItems]);

  const getItemError = (index: number) => ({
    [ITEM_FORM_FIELDS.NAME]: errors?.[`${FORM_FIELDS.ITEMS}[${index}].${ITEM_FORM_FIELDS.NAME}`],
    [ITEM_FORM_FIELDS.FIAT_AMOUNT]: errors?.[`${FORM_FIELDS.ITEMS}[${index}].${ITEM_FORM_FIELDS.FIAT_AMOUNT}`],
    [ITEM_FORM_FIELDS.IMAGE_URL]: errors?.[`${FORM_FIELDS.ITEMS}[${index}].${ITEM_FORM_FIELDS.IMAGE_URL}`],
  });

  const getImageError = (index: number) => {
    const uploadError = uploadErrors.find((upload) => upload.error && upload.id === Object.keys(items)[index]);
    const itemError = getItemError(index)?.[ITEM_FORM_FIELDS.IMAGE_URL];
    const backendValidationError = itemError ? ({ error: itemError } as ImageUploadOutcome) : undefined;

    return uploadError || backendValidationError;
  };

  const getInputForTypeForTaxItem = (item: Item, index: number, readonly?: boolean) => {
    const fieldError = errors?.[`${FORM_FIELDS.ITEMS}[${index + Object.keys(items).length}].${ITEM_FORM_FIELDS.FIAT_AMOUNT}`];
    return (
      <>
        <Input
          fullWidth
          type={InputType.Number}
          value={item.fiatAmount?.toString()}
          onChange={onTaxItemChange(item.id, ITEM_FORM_FIELDS.FIAT_AMOUNT)}
          disabled={readonly}
          placeholder={t(LABELS.ITEM_FORM_FIELDS.PLACEHOLDERS_FOR_TYPE[item.type]!)}
        />
        <OptionallyVisible visible={Boolean(fieldError)}>
          <ErrorMessage>{fieldError}</ErrorMessage>
        </OptionallyVisible>
      </>
    );
  };

  const getInputForTypeForDiscountItem = (item: Item, index: number, readonly?: boolean) => {
    const fieldError = errors?.[`${FORM_FIELDS.ITEMS}[${index + Object.keys(items).length}].${ITEM_FORM_FIELDS.FIAT_AMOUNT}`];
    return (
      <>
        <Input
          fullWidth
          type={InputType.Number}
          value={item.fiatAmount?.toString()}
          onChange={onDiscountItemChange(item.id, ITEM_FORM_FIELDS.FIAT_AMOUNT)}
          disabled={readonly}
          placeholder={t(LABELS.ITEM_FORM_FIELDS.PLACEHOLDERS_FOR_TYPE[item.type]!)}
        />
        <OptionallyVisible visible={Boolean(fieldError)}>
          <ErrorMessage>{fieldError}</ErrorMessage>
        </OptionallyVisible>
      </>
    );
  };

  const mapErrorMessagesToFormFieldIds = () => {
    const combinedItems = { ...items, ...taxItems, ...discountItems };
    return Object.entries(errors ?? {}).reduce((acc, [key, error]) => {
      const extractedIndex = key.match(/\d+/)?.[0];
      if (!extractedIndex) {
        return acc;
      }
      const failingFieldId = Object.values(combinedItems)[extractedIndex as any]?.id;

      return {
        ...acc,
        [failingFieldId]: error,
      };
    }, {});
  };

  const mapTaxItemsToFormFields = (taxItems: { [id: string]: Item }): FormFieldDescription[] =>
    Object.values(taxItems).map((item, index) => {
      const fieldError = errors?.[`${FORM_FIELDS.ITEMS}[${index + Object.keys(items).length}].${ITEM_FORM_FIELDS.NAME}`];

      return {
        key: item.id,
        label: t(LABELS.ITEM_FORM_FIELDS.LABELS_FOR_TYPE[item.type]!),
        renderInput: ({ readonly }) => (
          <FieldsWrapper>
            <OptionallyVisible visible={item.type === OrderItemType.Custom}>
              <Input
                fullWidth
                type={InputType.Text}
                value={item.name}
                onChange={onTaxItemChange(item.id, ITEM_FORM_FIELDS.NAME)}
                disabled={readonly}
                placeholder={t(LABELS.ITEM_FORM_FIELDS.CUSTOM_NAME_PLACEHOLDER)}
              />
              <OptionallyVisible visible={Boolean(fieldError)}>
                <ErrorMessage>{fieldError}</ErrorMessage>
              </OptionallyVisible>
            </OptionallyVisible>
            {getInputForTypeForTaxItem(item, index, readonly)}
          </FieldsWrapper>
        ),
        formRowItem: (
          <OptionallyVisible visible={item.type === OrderItemType.Custom}>
            <RemoveButton flat onClick={() => onRemoveTaxItem(item.id)}>
              <IconRemove /> {t(LABELS.ACTIONS.REMOVE_CUSTOM_TAX)}
            </RemoveButton>
          </OptionallyVisible>
        ),
      };
    });

  const mapDiscountItemsToFormFields = (discountItems: { [id: string]: Item }): FormFieldDescription[] =>
    Object.values(discountItems).map((item, index) => {
      const fieldError = errors?.[`${FORM_FIELDS.ITEMS}[${index + Object.keys(items).length}].${ITEM_FORM_FIELDS.NAME}`];
      return {
        key: item.id,
        label: t(LABELS.ITEM_FORM_FIELDS.LABELS_FOR_TYPE[item.type]!),
        renderInput: ({ readonly }) => (
          <FieldsWrapper>
            <OptionallyVisible visible={item.isRemovable}>
              <Input
                fullWidth
                type={InputType.Text}
                value={item.name}
                onChange={onDiscountItemChange(item.id, ITEM_FORM_FIELDS.NAME)}
                disabled={readonly}
                placeholder={t(LABELS.ITEM_FORM_FIELDS.CUSTOM_NAME_PLACEHOLDER)}
              />
              <OptionallyVisible visible={Boolean(fieldError)}>
                <ErrorMessage>{fieldError}</ErrorMessage>
              </OptionallyVisible>
            </OptionallyVisible>
            {getInputForTypeForDiscountItem(item, index, readonly)}
          </FieldsWrapper>
        ),
        formRowItem: (
          <OptionallyVisible visible={item.isRemovable}>
            <RemoveButton flat onClick={() => onRemoveDiscountItem(item.id)}>
              <IconRemove /> {t(LABELS.ACTIONS.REMOVE_CUSTOM_DISCOUNT)}
            </RemoveButton>
          </OptionallyVisible>
        ),
      };
    });

  return (
    <Panel label={t(LABELS.ORDER_SUMMARY_PANEL_TITLE)}>
      <PaddingWrapper>
        <LightText>{t(LABELS.ITEM_DESCRIPTION)}</LightText>
        {Object.entries(items).map(([id, item], index) => (
          <OrderSummaryItem
            key={id}
            item={item}
            onItemChange={onItemChange(id, OrderItemType.Product)}
            readOnlyMode={readOnlyMode}
            error={getItemError(index)}
            uploadError={getImageError(index)}
            onRemoveItem={() => onRemoveItem(id)}
            deleteable={index > 0}
          />
        ))}
        <Button flat onClick={onAddItem}>
          <IconAdd /> {t(LABELS.ACTIONS.ADD_PRODUCT)}
        </Button>
        <Separator />
        <LightText>{t(LABELS.TAX_DESCRIPTION)}</LightText>
        <FormWrapper>
          <FormLayout
            fields={mapTaxItemsToFormFields(taxItems)}
            readonly={readOnlyMode}
            error={mapErrorMessagesToFormFieldIds() as Record<string, string>}
          />
        </FormWrapper>
        <Button flat onClick={onAddTaxItem}>
          <IconAdd /> {t(LABELS.ACTIONS.ADD_CUSTOM_TAX)}
        </Button>
        <Separator />
        <LightText>{t(LABELS.DISCOUNT_DESCRIPTION)}</LightText>
        <FormWrapper>
          <FormLayout
            fields={mapDiscountItemsToFormFields(discountItems)}
            readonly={readOnlyMode}
            error={mapErrorMessagesToFormFieldIds() as Record<string, string>}
          />
        </FormWrapper>
        <Button flat onClick={onAddDiscountItem}>
          <IconAdd /> {t(LABELS.ACTIONS.ADD_CUSTOM_DISCOUNT)}
        </Button>
      </PaddingWrapper>
    </Panel>
  );
};

export default OrderSummary;
